import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Spinner, Modal } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

const EmailSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [currentSettings, setCurrentSettings] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  
  // Form state
  const [provider, setProvider] = useState('outlook');
  const [senderEmail, setSenderEmail] = useState('');
  const [testEmail, setTestEmail] = useState('');
  
  // Outlook (OAuth 2.0) için
  const [clientId, setClientId] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  
  // Gmail (App Password) için
  const [appPassword, setAppPassword] = useState('');
  
  // SMTP için
  const [smtpUsername, setSmtpUsername] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpServer, setSmtpServer] = useState('');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpUseSsl, setSmtpUseSsl] = useState(true);
  
  // Mevcut ayarları getir
  const fetchSettings = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      
      const { data } = await axios.get('/api/email/settings', config);
      
      if (data.success && data.settings) {
        setCurrentSettings(data.settings);
        setProvider(data.settings.provider);
        setSenderEmail(data.settings.senderEmail);
        
        // Sağlayıcıya göre alanları doldur
        if (data.settings.provider === 'outlook') {
          setTenantId(data.settings.tenantId || '');
        } else if (data.settings.provider === 'smtp') {
          setSmtpUsername(data.settings.smtpUsername || '');
          setSmtpServer(data.settings.smtpServer || '');
          setSmtpPort(data.settings.smtpPort || '587');
          setSmtpUseSsl(data.settings.smtpUseSsl !== undefined ? data.settings.smtpUseSsl : true);
        }
      } else {
        // Varsayılan değerler
        resetForm();
      }
    } catch (error) {
      toast.error('E-mail ayarları yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };
  
  // Sayfa yüklendiğinde ayarları getir
  useEffect(() => {
    fetchSettings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Ayarları değiştir butonuna tıklandığında
  const handleChangeSettings = () => {
    resetForm();
    setShowForm(true);
  };
  
  // Test başarılı olduğunda formu gizle
  useEffect(() => {
    if (currentSettings && !loading) {
      setShowForm(false);
    }
  }, [currentSettings, loading]);
  
  // Formu sıfırla
  const resetForm = () => {
    setProvider('outlook');
    setSenderEmail('');
    setTestEmail('');
    setClientId('');
    setTenantId('');
    setClientSecret('');
    setAppPassword('');
    setSmtpUsername('');
    setSmtpPassword('');
    setSmtpServer('');
    setSmtpPort('587');
    setSmtpUseSsl(true);
  };
  
  // Test et ve kaydet
  const handleTestAndSave = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setTesting(true);
      
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      
      // Sağlayıcıya göre veri hazırla
      const emailData = {
        provider,
        senderEmail,
        testEmail
      };
      
      if (provider === 'outlook') {
        emailData.clientId = clientId;
        emailData.tenantId = tenantId;
        emailData.clientSecret = clientSecret;
      } else if (provider === 'gmail') {
        emailData.appPassword = appPassword;
      } else if (provider === 'smtp') {
        emailData.smtpUsername = smtpUsername;
        emailData.smtpPassword = smtpPassword;
        emailData.smtpServer = smtpServer;
        emailData.smtpPort = parseInt(smtpPort);
        emailData.smtpUseSsl = smtpUseSsl;
      }
      
      // Önce test e-mail gönder
      const testResult = await axios.post('/api/email/test', emailData, config);
      
      if (testResult.data.success) {
        toast.success('Test e-mail başarıyla gönderildi');
        
        // Test başarılı olduysa ayarları kaydet
        setSaving(true);
        
        const saveResult = await axios.post('/api/email/settings', emailData, config);
        
        if (saveResult.data.success) {
          toast.success('E-mail ayarları başarıyla kaydedildi');
          setCurrentSettings(saveResult.data.settings);
        }
      }
    } catch (error) {
      const message = error.response?.data?.message || 'İşlem sırasında bir hata oluştu';
      toast.error(message);
    } finally {
      setTesting(false);
      setSaving(false);
    }
  };
  
  // Silme modalını aç
  const handleShowDeleteModal = () => {
    setShowDeleteModal(true);
  };
  
  // Silme modalını kapat
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };
  
  // Ayarları sil
  const handleDeleteSettings = async () => {
    try {
      setDeleting(true);
      
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      
      const { data } = await axios.delete('/api/email/settings', config);
      
      if (data.success) {
        toast.success('E-mail ayarları başarıyla silindi');
        setCurrentSettings(null);
        resetForm();
        handleCloseDeleteModal();
      }
    } catch (error) {
      const message = error.response?.data?.message || 'E-mail ayarları silinirken bir hata oluştu';
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };
  
  // Form doğrulama
  const validateForm = () => {
    if (!senderEmail) {
      toast.error('Gönderici e-mail adresi gereklidir');
      return false;
    }
    
    if (!testEmail) {
      toast.error('Test e-mail adresi gereklidir');
      return false;
    }
    
    if (provider === 'outlook') {
      if (!clientId || !tenantId || !clientSecret) {
        toast.error('Client ID, Tenant ID ve Client Secret gereklidir');
        return false;
      }
    } else if (provider === 'gmail') {
      if (!appPassword) {
        toast.error('App Password gereklidir');
        return false;
      }
    } else if (provider === 'smtp') {
      if (!smtpUsername || !smtpPassword || !smtpServer || !smtpPort) {
        toast.error('SMTP kullanıcı adı, şifre, sunucu ve port gereklidir');
        return false;
      }
    }
    
    return true;
  };
  
  // Sağlayıcı değiştiğinde ilgili alanları göster
  const renderProviderFields = () => {
    switch (provider) {
      case 'outlook':
        return (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Client ID</Form.Label>
              <Form.Control
                type="text"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                required
                autoComplete="off"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Tenant ID</Form.Label>
              <Form.Control
                type="text"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                required
                autoComplete="off"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Client Secret</Form.Label>
              <Form.Control
                type="password"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                required
                autoComplete="off"
              />
            </Form.Group>
          </>
        );
      
      case 'gmail':
        return (
          <Form.Group className="mb-3">
            <Form.Label>App Password</Form.Label>
            <Form.Control
              type="password"
              value={appPassword}
              onChange={(e) => setAppPassword(e.target.value)}
              required
              autoComplete="off"
            />
            <Form.Text className="text-muted">
              Gmail App Password oluşturmak için <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer">buraya tıklayın</a>.
            </Form.Text>
          </Form.Group>
        );
      
      case 'smtp':
        return (
          <>
            <Form.Group className="mb-3">
              <Form.Label>SMTP Kullanıcı Adı</Form.Label>
              <Form.Control
                type="text"
                value={smtpUsername}
                onChange={(e) => setSmtpUsername(e.target.value)}
                required
                autoComplete="off"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>SMTP Şifresi</Form.Label>
              <Form.Control
                type="password"
                value={smtpPassword}
                onChange={(e) => setSmtpPassword(e.target.value)}
                required
                autoComplete="off"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>SMTP Sunucusu</Form.Label>
              <Form.Control
                type="text"
                value={smtpServer}
                onChange={(e) => setSmtpServer(e.target.value)}
                required
                autoComplete="off"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>SMTP Portu</Form.Label>
              <Form.Control
                type="number"
                value={smtpPort}
                onChange={(e) => setSmtpPort(e.target.value)}
                required
                autoComplete="off"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="SSL/TLS Kullan"
                checked={smtpUseSsl}
                onChange={(e) => setSmtpUseSsl(e.target.checked)}
              />
            </Form.Group>
          </>
        );
      
      default:
        return null;
    }
  };
  
  return (
    <>
      <Card>
        <Card.Body>
          <Card.Title>E-mail Ayarları</Card.Title>
          
          {loading ? (
            <div className="text-center my-3">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </Spinner>
            </div>
          ) : currentSettings && !showForm ? (
            <div>
              <div className="mb-4">
                <h4 className="mb-3">
                  {currentSettings.provider === 'outlook' ? 'Outlook' : 
                   currentSettings.provider === 'gmail' ? 'Gmail' : 
                   'SMTP'}: {currentSettings.senderEmail}
                </h4>
                <p className="text-muted">
                  {currentSettings.provider === 'outlook' ? 'Outlook (OAuth 2.0) ile e-mail gönderimi yapılandırıldı.' : 
                   currentSettings.provider === 'gmail' ? 'Gmail (App Password) ile e-mail gönderimi yapılandırıldı.' : 
                   'SMTP ile e-mail gönderimi yapılandırıldı.'}
                </p>
              </div>
              
              <div className="d-flex justify-content-between">
                <Button 
                  variant="danger" 
                  onClick={handleShowDeleteModal}
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Siliniyor...
                    </>
                  ) : (
                    'E-mail Ayarlarını Sil'
                  )}
                </Button>
                
                <Button 
                  variant="primary" 
                  onClick={handleChangeSettings}
                >
                  E-mail Ayarlarını Değiştir
                </Button>
              </div>
            </div>
          ) : (
            <Form onSubmit={handleTestAndSave}>
              <Form.Group className="mb-3">
                <Form.Label>E-mail Sağlayıcı</Form.Label>
                <Form.Select
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                  required
                >
                  <option value="outlook">Outlook (OAuth 2.0)</option>
                  <option value="gmail">Gmail (App Password)</option>
                  <option value="smtp">SMTP</option>
                </Form.Select>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Gönderici E-mail Adresi</Form.Label>
                <Form.Control
                  type="email"
                  value={senderEmail}
                  onChange={(e) => setSenderEmail(e.target.value)}
                  required
                  autoComplete="off"
                  autoFocus
                />
              </Form.Group>
              
              {renderProviderFields()}
              
              <Form.Group className="mb-3">
                <Form.Label>Test E-mail Adresi</Form.Label>
                <Form.Control
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  required
                  autoComplete="off"
                />
              </Form.Group>
              
              <div className="d-flex justify-content-between">
                <Button 
                  variant="secondary" 
                  onClick={() => setShowForm(false)}
                  disabled={!currentSettings}
                >
                  İptal
                </Button>
                
                <Button 
                  variant="success" 
                  type="submit"
                  disabled={testing || saving}
                >
                  {testing ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Test Ediliyor...
                    </>
                  ) : saving ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Kaydediliyor...
                    </>
                  ) : (
                    'Test & Kaydet'
                  )}
                </Button>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>
      
      {/* Silme Onay Modalı */}
      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
        <Modal.Header closeButton>
          <Modal.Title>E-mail Ayarlarını Sil</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>E-mail ayarlarını silmek istediğinize emin misiniz?</p>
          <p className="text-danger">Bu işlem geri alınamaz!</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            İptal
          </Button>
          <Button variant="danger" onClick={handleDeleteSettings} disabled={deleting}>
            {deleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Siliniyor...
              </>
            ) : (
              'Sil'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EmailSettings;
